/**
 * Trainer in-room panel: join defaults + focus / PiP policy.
 */

import React, { useEffect, useState } from "react";
import { livekitAuthService as authService } from "../utils/authService";
import {
  normalizeSessionSettings,
  type LiveSessionSettings,
} from "./sessionSettings";

type Props = {
  sessionId: string | number;
  initialSettings?: unknown;
  onSaved?: (settings: LiveSessionSettings) => void;
};

export function MediaJoinSettingsPanel({
  sessionId,
  initialSettings,
  onSaved,
}: Props) {
  const [settings, setSettings] = useState<LiveSessionSettings>(() =>
    normalizeSessionSettings(initialSettings),
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSettings(normalizeSessionSettings(initialSettings));
  }, [initialSettings]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    const payload = normalizeSessionSettings(settings);
    // Apply to students immediately via LiveKit policy sync; persist after.
    onSaved?.(payload);
    try {
      const res = await authService.put("/session/update/", {
        session_id: String(sessionId),
        session_settings: payload,
      });
      if (res?.data?.success === false) {
        setError(res?.data?.message || "Failed to save settings");
        return;
      }
      const saved = normalizeSessionSettings(
        res?.data?.session?.session_settings ?? payload,
      );
      setSettings(saved);
      onSaved?.(saved);
      setMessage("Settings saved and applied for students in this session.");
    } catch (err) {
      console.error("save session_settings failed", err);
      setError("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (
    role: "trainer" | "student",
    field: "microphone" | "camera",
  ) => {
    setSettings((prev) => ({
      ...prev,
      join_defaults: {
        ...prev.join_defaults,
        [role]: {
          ...prev.join_defaults[role],
          [field]: !prev.join_defaults[role][field],
        },
      },
    }));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <section
        style={{
          padding: 12,
          borderRadius: 10,
          border: "1px solid rgba(255,255,255,0.08)",
          background: "rgba(255,255,255,0.03)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
          Focus &amp; Picture-in-Picture
        </div>
        <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", lineHeight: 1.45 }}>
          You control whether students may leave the tab and whether the session
          auto-opens in Picture-in-Picture. Students never get a PiP button.
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 12,
            color: "#e2e8f0",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={settings.distraction_detection_enabled}
            onChange={() =>
              setSettings((prev) => {
                const nextOn = !prev.distraction_detection_enabled;
                return {
                  ...prev,
                  distraction_detection_enabled: nextOn,
                  student_pip_allowed: nextOn
                    ? false
                    : prev.student_pip_allowed || true,
                };
              })
            }
            style={{ marginTop: 2 }}
          />
          <span>
            <strong style={{ color: "#fff" }}>Distraction detection</strong>
            <br />
            When on, leaving this session tab is recorded as a violation.
            Students cannot use Picture-in-Picture.
          </span>
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 12,
            color: settings.distraction_detection_enabled
              ? "#64748b"
              : "#e2e8f0",
            cursor: settings.distraction_detection_enabled
              ? "not-allowed"
              : "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={
              !settings.distraction_detection_enabled &&
              settings.student_pip_allowed
            }
            disabled={settings.distraction_detection_enabled}
            onChange={() =>
              setSettings((prev) => ({
                ...prev,
                student_pip_allowed: !prev.student_pip_allowed,
              }))
            }
            style={{ marginTop: 2 }}
          />
          <span>
            <strong
              style={{
                color: settings.distraction_detection_enabled
                  ? "#64748b"
                  : "#fff",
              }}
            >
              Allow student Picture-in-Picture
            </strong>
            <br />
            When distraction detection is off, the session opens in PiP
            automatically so students can use other tabs while staying in the
            call.
          </span>
        </label>
      </section>

      <p style={{ margin: 0, fontSize: 12, color: "#94a3b8", lineHeight: 1.45 }}>
        Control whether mic and camera start on or off for the pre-join device
        check. Participants can still change them before joining.
      </p>

      {(
        [
          ["trainer", "Trainer"],
          ["student", "Student"],
        ] as const
      ).map(([role, label]) => (
        <div
          key={role}
          style={{
            padding: 12,
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#fff",
              marginBottom: 8,
            }}
          >
            {label} defaults
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: "#e2e8f0",
              marginBottom: 6,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={settings.join_defaults[role].microphone}
              onChange={() => toggle(role, "microphone")}
            />
            Microphone on
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: "#e2e8f0",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={settings.join_defaults[role].camera}
              onChange={() => toggle(role, "camera")}
            />
            Camera on
          </label>
        </div>
      ))}

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          color: "#e2e8f0",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={settings.waiting_room_enabled}
          onChange={() =>
            setSettings((prev) => ({
              ...prev,
              waiting_room_enabled: !prev.waiting_room_enabled,
            }))
          }
        />
        Waiting room (admit students before they can publish)
      </label>

      {error ? (
        <p style={{ margin: 0, fontSize: 12, color: "#fca5a5" }}>{error}</p>
      ) : null}
      {message ? (
        <p style={{ margin: 0, fontSize: 12, color: "#86efac" }}>{message}</p>
      ) : null}

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        style={{
          padding: "10px 12px",
          borderRadius: 10,
          border: "none",
          background: saving ? "#334155" : "#2563eb",
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          cursor: saving ? "wait" : "pointer",
        }}
      >
        {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}
